'use client'

// src/sparkline.tsx
import { Box, useTheme } from "@chakra-ui/react";
import { AreaChart, Area, ResponsiveContainer, YAxis } from "recharts";

// src/utils.ts
var createCategoryColors = (categories, colors, theme) => {
  return Object.fromEntries(
    categories.map((category, index) => {
      var _a;
      const color = getColor((_a = colors[index]) != null ? _a : "gray.500", theme);
      return [category, color];
    })
  );
};
var getColor = (color, theme) => {
  var _a;
  if (color.match(/\.[0-9]{2,3}/)) {
    return `var(--chakra-colors-${color.replace(".", "-")})`;
  } else if (theme.colors[color]) {
    return (_a = theme.colors[color]) == null ? void 0 : _a[500];
  }
  return color;
};

// src/sparkline.tsx
import { useId } from "react";
import { jsx, jsxs } from "react/jsx-runtime";
var Sparkline = (props) => {
  const {
    data = [],
    categories = ["value"],
    curveType,
    colors = ["primary", "gray"],
    strokeWidth = 1,
    variant = "gradient",
    showAnimation = false,
    animationDuration = 500,
    connectNulls = true,
    stack = false,
    ...rest
  } = props;
  const theme = useTheme();
  const id = useId();
  const categoryColors = createCategoryColors(categories, colors, theme);
  const getColor2 = (category) => {
    return categoryColors[category];
  };
  const getGradientId = (category) => {
    return `${id}-${category}-gradient`;
  };
  const getFill = (category) => {
    switch (variant) {
      case "solid":
        return getColor2(category);
      case "gradient":
        return `url(#${getGradientId(category)})`;
      default:
        return "transparent";
    }
  };
  return /* @__PURE__ */ jsx(
    Box,
    {
      ...rest,
      sx: {
        "--sparkline-fill-opacity": "0.2",
        "--sparkline-gradient-start-opacity": "0.8",
        "--sparkline-gradient-end-opacity": "0",
        ...rest.sx
      },
      children: /* @__PURE__ */ jsx(ResponsiveContainer, { width: "100%", height: "100%", minWidth: "0", children: /* @__PURE__ */ jsxs(
        AreaChart,
        {
          data,
          margin: { top: 2, right: 0, left: 0, bottom: 0 },
          children: [
            /* @__PURE__ */ jsx("defs", { children: categories.map((category) => /* @__PURE__ */ jsxs(
              "linearGradient",
              {
                id: getGradientId(category),
                x1: "0",
                y1: "0",
                x2: "0",
                y2: "1",
                children: [
                  /* @__PURE__ */ jsx(
                    "stop",
                    {
                      offset: "5%",
                      stopColor: getColor2(category),
                      stopOpacity: "var(--sparkline-gradient-start-opacity))"
                    }
                  ),
                  /* @__PURE__ */ jsx(
                    "stop",
                    {
                      offset: "95%",
                      stopColor: getColor2(category),
                      stopOpacity: "var(--sparkline-gradient-end-opacity)"
                    }
                  )
                ]
              },
              category
            )) }),
            /* @__PURE__ */ jsx(
              YAxis,
              {
                tick: false,
                axisLine: false,
                width: 0,
                domain: ["dataMin", "dataMax"]
              }
            ),
            categories.map((category) => /* @__PURE__ */ jsx(
              Area,
              {
                dataKey: category,
                stroke: getColor2(category),
                strokeWidth,
                fill: getFill(category),
                fillOpacity: "var(--sparkline-fill-opacity)",
                type: curveType,
                isAnimationActive: showAnimation,
                animationDuration,
                connectNulls,
                stackId: stack ? "a" : void 0
              },
              category
            ))
          ]
        }
      ) })
    }
  );
};
var Sparklines = Sparkline;

// src/area-chart.tsx
import * as React2 from "react";
import { Box as Box4, useColorModeValue, useTheme as useTheme2 } from "@chakra-ui/react";
import {
  AreaChart as ReAreaChart,
  Area as Area2,
  XAxis,
  YAxis as YAxis2,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer as ResponsiveContainer2,
  Legend
} from "recharts";

// src/legend.tsx
import { Box as Box2, HStack, forwardRef } from "@chakra-ui/react";
import { jsx as jsx2, jsxs as jsxs2 } from "react/jsx-runtime";
var ChartLegend = forwardRef(
  ({ payload, categoryColors }, ref) => {
    return /* @__PURE__ */ jsx2(HStack, { ref, justifyContent: "flex-end", spacing: "2", children: payload == null ? void 0 : payload.map((entry, index) => {
      var _a;
      return /* @__PURE__ */ jsxs2(HStack, { spacing: "1", children: [
        /* @__PURE__ */ jsx2(
          Box2,
          {
            rounded: "full",
            bg: (_a = categoryColors[entry.value]) != null ? _a : entry.color,
            boxSize: "2"
          }
        ),
        /* @__PURE__ */ jsx2(Box2, { as: "span", children: entry.value })
      ] }, `item-${index}`);
    }) });
  }
);

// src/tooltip.tsx
import { Box as Box3, HStack as HStack2, Text, useStyleConfig } from "@chakra-ui/react";
import React from "react";
import { jsx as jsx3, jsxs as jsxs3 } from "react/jsx-runtime";
var ChartTooltip = (props) => {
  const {
    categoryColors,
    contentStyle = {},
    itemStyle = {},
    labelStyle = {},
    payload,
    formatter,
    wrapperClassName,
    labelClassName,
    label,
    labelFormatter
  } = props;
  const tooltipTheme = useStyleConfig("Tooltip");
  const renderContent = () => {
    if (payload && payload.length) {
      const items = payload.map((entry, i) => {
        var _a, _b;
        if (entry.type === "none" || entry.value === void 0 || entry.name === void 0) {
          return null;
        }
        const finalItemStyle = {
          paddingTop: 2,
          paddingBottom: 2,
          ...itemStyle
        };
        const finalFormatter = entry.formatter || formatter;
        const { value, name } = entry;
        let finalValue = value;
        let finalName = name;
        if (finalFormatter && finalValue != null && finalName != null) {
          const formatted = (_a = finalFormatter == null ? void 0 : finalFormatter(value, name, entry, i, payload)) != null ? _a : value;
          if (Array.isArray(formatted)) {
            ;
            [finalValue, finalName] = formatted;
          } else {
            finalValue = formatted;
          }
        }
        return /* @__PURE__ */ jsxs3(
          HStack2,
          {
            as: "li",
            style: finalItemStyle,
            spacing: "1",
            children: [
              /* @__PURE__ */ jsx3(
                Box3,
                {
                  rounded: "full",
                  bg: (_b = categoryColors[entry.name]) != null ? _b : entry.color,
                  boxSize: "2"
                }
              ),
              finalName ? /* @__PURE__ */ jsx3(
                Box3,
                {
                  flex: "1",
                  minWidth: "80px",
                  color: "var(--tooltip-fg-muted, var(--chakra-colors-muted))",
                  children: finalName
                }
              ) : null,
              /* @__PURE__ */ jsxs3(Box3, { textAlign: "right", children: [
                /* @__PURE__ */ jsx3("span", { children: finalValue }),
                /* @__PURE__ */ jsx3("span", { children: entry.unit || "" })
              ] })
            ]
          },
          `tooltip-item-${i}`
        );
      });
      return /* @__PURE__ */ jsx3(Box3, { as: "ul", children: items });
    }
    return null;
  };
  const hasLabel = !!label;
  let finalLabel = hasLabel ? label : "";
  if (hasLabel && labelFormatter && payload !== void 0 && payload !== null) {
    finalLabel = labelFormatter(label, payload);
  }
  return /* @__PURE__ */ jsxs3(
    Box3,
    {
      outline: "none",
      flexDirection: "column",
      className: wrapperClassName,
      style: contentStyle,
      sx: tooltipTheme,
      children: [
        /* @__PURE__ */ jsx3(Text, { w: "full", className: labelClassName, style: labelStyle, children: React.isValidElement(finalLabel) ? finalLabel : `${finalLabel}` }),
        renderContent()
      ]
    }
  );
};

// src/area-chart.tsx
import { jsx as jsx4, jsxs as jsxs4 } from "react/jsx-runtime";
var AreaChart2 = React2.forwardRef(
  (props, ref) => {
    const {
      data = [],
      categories = [],
      colors = ["primary", "cyan"],
      height,
      connectNulls = false,
      curveType = "linear",
      index = "date",
      startEndOnly = false,
      intervalType = "equidistantPreserveStart",
      allowDecimals = true,
      strokeWidth = 2,
      showAnimation = true,
      showGrid = true,
      showLegend = true,
      showTooltip = true,
      showXAxis = true,
      showYAxis = true,
      stack = false,
      stackOffset,
      yAxisWidth = 40,
      legendHeight = 32,
      animationDuration = 500,
      valueFormatter,
      variant = "gradient",
      tooltipContent,
      children,
      minValue = 0,
      maxValue = "auto"
    } = props;
    const theme = useTheme2();
    const id = React2.useId();
    const categoryColors = createCategoryColors(categories, colors, theme);
    const getColor2 = (category) => {
      return categoryColors[category];
    };
    const getGradientId = (category) => {
      return `${id}-${category}-gradient`;
    };
    const getFill = (category) => {
      switch (variant) {
        case "solid":
          return getColor2(category);
        case "gradient":
          return `url(#${getGradientId(category)})`;
        default:
          return "transparent";
      }
    };
    const yAxisDomain = [minValue, maxValue];
    return /* @__PURE__ */ jsx4(
      Box4,
      {
        ref,
        height,
        fontSize: "sm",
        sx: {
          "--chart-gradient-start-opacity": "0.8",
          "--chart-gradient-end-opacity": "0"
        },
        children: /* @__PURE__ */ jsx4(ResponsiveContainer2, { width: "100%", height: "100%", minWidth: "0", children: /* @__PURE__ */ jsxs4(ReAreaChart, { data, stackOffset, children: [
          showGrid && /* @__PURE__ */ jsx4(
            CartesianGrid,
            {
              strokeDasharray: " 1 1 1",
              vertical: false,
              strokeOpacity: useColorModeValue(0.8, 0.3)
            }
          ),
          /* @__PURE__ */ jsx4(
            XAxis,
            {
              padding: { left: 20, right: 20 },
              dataKey: index,
              hide: !showXAxis,
              tick: { transform: "translate(0, 6)" },
              ticks: startEndOnly ? [data[0][index], data[data.length - 1][index]] : void 0,
              interval: startEndOnly ? "preserveStartEnd" : intervalType,
              tickLine: false,
              axisLine: false,
              minTickGap: 5,
              style: {
                color: "var(--chakra-colors-muted)"
              }
            }
          ),
          /* @__PURE__ */ jsx4(
            YAxis2,
            {
              width: yAxisWidth,
              hide: !showYAxis,
              axisLine: false,
              tickLine: false,
              tick: { transform: "translate(-3, 0)" },
              tickCount: 6,
              type: "number",
              tickFormatter: valueFormatter,
              allowDecimals,
              domain: yAxisDomain,
              style: {
                color: "var(--chakra-colors-muted)"
              }
            }
          ),
          showTooltip && /* @__PURE__ */ jsx4(
            Tooltip,
            {
              formatter: valueFormatter,
              content: tooltipContent ? tooltipContent : (props2) => /* @__PURE__ */ jsx4(
                ChartTooltip,
                {
                  ...props2,
                  categoryColors
                }
              )
            }
          ),
          showLegend && /* @__PURE__ */ jsx4(
            Legend,
            {
              verticalAlign: "top",
              align: "right",
              height: legendHeight,
              content: ({ payload }) => {
                return /* @__PURE__ */ jsx4(
                  ChartLegend,
                  {
                    payload,
                    categoryColors
                  }
                );
              }
            }
          ),
          /* @__PURE__ */ jsx4("defs", { children: categories.map((category) => /* @__PURE__ */ jsxs4(
            "linearGradient",
            {
              id: getGradientId(category),
              x1: "0",
              y1: "0",
              x2: "0",
              y2: "1",
              children: [
                /* @__PURE__ */ jsx4(
                  "stop",
                  {
                    offset: "5%",
                    stopColor: getColor2(category),
                    stopOpacity: "var(--chart-gradient-start-opacity)"
                  }
                ),
                /* @__PURE__ */ jsx4(
                  "stop",
                  {
                    offset: "95%",
                    stopColor: getColor2(category),
                    stopOpacity: "var(--chart-gradient-end-opacity)"
                  }
                )
              ]
            },
            category
          )) }),
          children,
          categories.map((category) => /* @__PURE__ */ jsx4(
            Area2,
            {
              type: curveType,
              dataKey: category,
              stroke: getColor2(category),
              strokeWidth,
              strokeLinejoin: "round",
              strokeLinecap: "round",
              fill: getFill(category),
              isAnimationActive: showAnimation,
              animationDuration,
              stackId: stack ? "a" : void 0,
              connectNulls
            },
            category
          ))
        ] }) })
      }
    );
  }
);

// src/line-chart.tsx
import * as React3 from "react";
import { Box as Box5, useColorModeValue as useColorModeValue2, useTheme as useTheme3 } from "@chakra-ui/react";
import {
  LineChart as ReLineChart,
  Line,
  XAxis as XAxis2,
  YAxis as YAxis3,
  CartesianGrid as CartesianGrid2,
  Tooltip as Tooltip2,
  ResponsiveContainer as ResponsiveContainer3,
  Legend as Legend2
} from "recharts";
import { jsx as jsx5, jsxs as jsxs5 } from "react/jsx-runtime";
var LineChart = React3.forwardRef(
  (props, ref) => {
    const {
      data = [],
      categories = [],
      colors = ["primary"],
      height,
      connectNulls = false,
      curveType = "linear",
      dot = false,
      index = "date",
      startEndOnly = false,
      intervalType = "equidistantPreserveStart",
      allowDecimals = true,
      strokeWidth = 2,
      showAnimation = true,
      showGrid = true,
      showLegend = true,
      showTooltip = true,
      showXAxis = true,
      showYAxis = true,
      yAxisWidth = 40,
      legendHeight = 32,
      animationDuration = 500,
      minValue = 0,
      maxValue = "auto",
      valueFormatter,
      tooltipContent,
      children
    } = props;
    const theme = useTheme3();
    const categoryColors = createCategoryColors(categories, colors, theme);
    const getColor2 = (category) => {
      return categoryColors[category];
    };
    const yAxisDomain = [minValue, maxValue];
    return /* @__PURE__ */ jsx5(Box5, { ref, height, fontSize: "sm", children: /* @__PURE__ */ jsx5(ResponsiveContainer3, { width: "100%", height: "100%", minWidth: "0", children: /* @__PURE__ */ jsxs5(ReLineChart, { data, children: [
      showGrid && /* @__PURE__ */ jsx5(
        CartesianGrid2,
        {
          strokeDasharray: " 1 1 1",
          vertical: false,
          strokeOpacity: useColorModeValue2(0.8, 0.3)
        }
      ),
      /* @__PURE__ */ jsx5(
        XAxis2,
        {
          padding: { left: 20, right: 20 },
          dataKey: index,
          hide: !showXAxis,
          tick: { transform: "translate(0, 6)" },
          ticks: startEndOnly ? [data[0][index], data[data.length - 1][index]] : void 0,
          interval: startEndOnly ? "preserveStartEnd" : intervalType,
          tickLine: false,
          axisLine: false,
          minTickGap: 5,
          style: {
            color: "var(--chakra-colors-muted)"
          }
        }
      ),
      /* @__PURE__ */ jsx5(
        YAxis3,
        {
          width: yAxisWidth,
          hide: !showYAxis,
          axisLine: false,
          tickLine: false,
          domain: yAxisDomain,
          tick: { transform: "translate(-3, 0)" },
          tickCount: 6,
          type: "number",
          tickFormatter: valueFormatter,
          allowDecimals,
          style: {
            color: "var(--chakra-colors-muted)"
          }
        }
      ),
      showTooltip && /* @__PURE__ */ jsx5(
        Tooltip2,
        {
          formatter: valueFormatter,
          content: tooltipContent ? tooltipContent : (props2) => /* @__PURE__ */ jsx5(
            ChartTooltip,
            {
              ...props2,
              categoryColors
            }
          )
        }
      ),
      showLegend && /* @__PURE__ */ jsx5(
        Legend2,
        {
          verticalAlign: "top",
          align: "right",
          height: legendHeight,
          content: ({ payload }) => {
            return /* @__PURE__ */ jsx5(
              ChartLegend,
              {
                payload,
                categoryColors
              }
            );
          }
        }
      ),
      children,
      categories.map((category) => /* @__PURE__ */ jsx5(
        Line,
        {
          type: curveType,
          dataKey: category,
          dot,
          stroke: getColor2(category),
          strokeWidth,
          strokeLinejoin: "round",
          strokeLinecap: "round",
          isAnimationActive: showAnimation,
          animationDuration,
          connectNulls
        },
        category
      ))
    ] }) }) });
  }
);

// src/bar-chart.tsx
import * as React4 from "react";
import { Box as Box6, useColorModeValue as useColorModeValue3, useTheme as useTheme4 } from "@chakra-ui/react";
import {
  BarChart as ReBarChart,
  Bar,
  XAxis as XAxis3,
  YAxis as YAxis4,
  CartesianGrid as CartesianGrid3,
  Tooltip as Tooltip3,
  ResponsiveContainer as ResponsiveContainer4,
  Legend as Legend3
} from "recharts";
import { jsx as jsx6, jsxs as jsxs6 } from "react/jsx-runtime";
var BarChart = React4.forwardRef(
  (props, ref) => {
    const {
      data = [],
      categories = [],
      colors = ["primary", "cyan"],
      height,
      index = "date",
      barGap = "2",
      barCategoryGap = "10%",
      barSize,
      startEndOnly = false,
      intervalType = "equidistantPreserveStart",
      allowDecimals = true,
      showAnimation = true,
      showGrid = true,
      showLegend = true,
      showTooltip = true,
      showXAxis = true,
      showYAxis = true,
      stack = false,
      stackOffset,
      yAxisWidth = 40,
      legendHeight = 32,
      animationDuration = 500,
      minValue = 0,
      maxValue = "auto",
      valueFormatter,
      variant = "gradient",
      tooltipContent,
      radius = stack ? 0 : [2, 2, 0, 0],
      children
    } = props;
    const theme = useTheme4();
    const id = React4.useId();
    const categoryColors = createCategoryColors(categories, colors, theme);
    const getColor2 = (category) => {
      return categoryColors[category];
    };
    const getGradientId = (category) => {
      return `${id}-${category}-gradient`;
    };
    const getFill = (category) => {
      switch (variant) {
        case "solid":
          return getColor2(category);
        case "gradient":
          return `url(#${getGradientId(category)})`;
        default:
          return "transparent";
      }
    };
    const yAxisDomain = [minValue, maxValue];
    return /* @__PURE__ */ jsx6(
      Box6,
      {
        ref,
        height,
        fontSize: "sm",
        sx: {
          "--chart-cursor-bg": "var(--chakra-colors-blackAlpha-100)",
          "--chart-gradient-start-opacity": "0.8",
          "--chart-gradient-end-opacity": "80",
          _dark: {
            "--chart-cursor-bg": "var(--chakra-colors-whiteAlpha-100)",
            "--chart-gradient-start-opacity": "80",
            "--chart-gradient-end-opacity": "0.8"
          }
        },
        children: /* @__PURE__ */ jsx6(ResponsiveContainer4, { width: "100%", height: "100%", minWidth: "0", children: /* @__PURE__ */ jsxs6(
          ReBarChart,
          {
            data,
            barCategoryGap,
            barGap,
            barSize,
            stackOffset,
            children: [
              showGrid && /* @__PURE__ */ jsx6(
                CartesianGrid3,
                {
                  strokeDasharray: " 1 1 1",
                  vertical: false,
                  strokeOpacity: useColorModeValue3(0.8, 0.3)
                }
              ),
              /* @__PURE__ */ jsx6(
                XAxis3,
                {
                  padding: { left: 20, right: 20 },
                  dataKey: index,
                  hide: !showXAxis,
                  tick: { transform: "translate(0, 6)" },
                  ticks: startEndOnly ? [data[0][index], data[data.length - 1][index]] : void 0,
                  interval: startEndOnly ? "preserveStartEnd" : intervalType,
                  tickLine: false,
                  axisLine: false,
                  minTickGap: 5,
                  style: {
                    color: "var(--chakra-colors-muted)"
                  }
                }
              ),
              /* @__PURE__ */ jsx6(
                YAxis4,
                {
                  width: yAxisWidth,
                  hide: !showYAxis,
                  axisLine: false,
                  tickLine: false,
                  tick: { transform: "translate(-3, 0)" },
                  tickCount: 6,
                  type: "number",
                  tickFormatter: valueFormatter,
                  domain: yAxisDomain,
                  allowDecimals,
                  style: {
                    color: "var(--chakra-colors-muted)"
                  }
                }
              ),
              showTooltip && /* @__PURE__ */ jsx6(
                Tooltip3,
                {
                  formatter: valueFormatter,
                  cursor: { fill: "var(--chart-cursor-bg)" },
                  content: tooltipContent ? tooltipContent : (props2) => /* @__PURE__ */ jsx6(
                    ChartTooltip,
                    {
                      ...props2,
                      categoryColors
                    }
                  )
                }
              ),
              showLegend && /* @__PURE__ */ jsx6(
                Legend3,
                {
                  verticalAlign: "top",
                  align: "right",
                  height: legendHeight,
                  content: ({ payload }) => {
                    return /* @__PURE__ */ jsx6(
                      ChartLegend,
                      {
                        payload,
                        categoryColors
                      }
                    );
                  }
                }
              ),
              /* @__PURE__ */ jsx6("defs", { children: categories.map((category) => /* @__PURE__ */ jsxs6(
                "linearGradient",
                {
                  id: getGradientId(category),
                  x1: "0",
                  y1: "0",
                  x2: "0",
                  y2: "1",
                  children: [
                    /* @__PURE__ */ jsx6(
                      "stop",
                      {
                        offset: "5%",
                        stopColor: getColor2(category),
                        stopOpacity: "var(--chart-gradient-start-opacity)"
                      }
                    ),
                    /* @__PURE__ */ jsx6(
                      "stop",
                      {
                        offset: "95%",
                        stopColor: getColor2(category),
                        stopOpacity: "var(--chart-gradient-end-opacity)"
                      }
                    )
                  ]
                },
                category
              )) }),
              children,
              categories.map((category) => /* @__PURE__ */ jsx6(
                Bar,
                {
                  dataKey: category,
                  fill: getFill(category),
                  color: getColor2(category),
                  isAnimationActive: showAnimation,
                  animationDuration,
                  stackId: stack ? "a" : void 0,
                  radius
                },
                category
              ))
            ]
          }
        ) })
      }
    );
  }
);
export {
  AreaChart2 as AreaChart,
  BarChart,
  ChartTooltip,
  LineChart,
  Sparkline,
  Sparklines
};
//# sourceMappingURL=index.mjs.map